import { computed, signal, Service } from '@angular/core';
import {
  clampPosition,
  defaultPositionForNewTile,
} from '../grid-layout/grid-position.util';
import {
  DEFAULT_TIME_CONTEXT,
  isValidTimeOverride,
} from '../services/time-range-options.util';
import type {
  Dashboard,
  DashboardVisibility,
  DashboardWidget,
  CreateDashboardRequest,
  DashboardWidgetType,
  TimeOverrideConfig,
  UpdateDashboardRequest,
} from '../types/dashboard.types';
import {
  TITLE_MAX_LENGTH,
  type DashboardFormState,
  type DashboardFormValue,
} from './dashboard-metadata';
import type {
  PlacedWidget,
  PlacedWidgetPosition,
} from './grid-layout/grid-layout.component';
import type { WidgetDefinition } from './widget-builder/widget-builder.component';
import { mergeChartConfig } from './widget-builder/widget-config-to-draft.util';

export type DashboardEditLoadStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'not-found'
  | 'error';

const VISIBILITY_VALUES: readonly DashboardVisibility[] = ['all', 'restricted'];

function normalizeVisibility(
  value: string | null | undefined
): DashboardVisibility {
  return VISIBILITY_VALUES.includes(value as DashboardVisibility)
    ? (value as DashboardVisibility)
    : 'all';
}

function toFormValue(dashboard: Dashboard): DashboardFormValue {
  return {
    title: dashboard.title ?? '',
    description: dashboard.description ?? null,
    visibility: normalizeVisibility(dashboard.visibility),
    departments: [...(dashboard.departments ?? [])],
    assignedUsers: [...(dashboard.assignedUsers ?? [])],
  };
}

export const NEW_DASHBOARD_TITLE = 'New dashboard';

/** Placeholder preview id for an unsaved dashboard, and the endpoints validate a GUID but never read it. */
export const NEW_DASHBOARD_PREVIEW_ID = '00000000-0000-0000-0000-000000000000';

function newDashboardFormValue(): DashboardFormValue {
  return {
    title: NEW_DASHBOARD_TITLE,
    description: null,
    visibility: 'restricted',
    departments: [],
    assignedUsers: [],
  };
}

function normalizeMetadata(value: DashboardFormValue): DashboardFormValue {
  const visibility = value.visibility ?? 'all';
  const restricted = visibility === 'restricted';
  const description = (value.description ?? '').trim();
  return {
    title: (value.title ?? '').trim(),
    description: description.length > 0 ? description : null,
    visibility,
    departments: restricted ? [...(value.departments ?? [])] : [],
    assignedUsers: restricted ? [...(value.assignedUsers ?? [])] : [],
  };
}

function isMetadataValid(value: DashboardFormValue): boolean {
  const title = (value.title ?? '').trim();
  return title.length > 0 && title.length <= TITLE_MAX_LENGTH;
}

function toPlacedWidgets(
  widgets: DashboardWidget[] | null | undefined
): PlacedWidget[] {
  if (!Array.isArray(widgets)) return [];
  return widgets
    .filter(widget => !!widget?.id)
    .map(widget => ({
      localId: widget.id,
      serverId: widget.id,
      title: widget.title ?? '',
      widgetType: widget.widgetType as DashboardWidgetType,
      queryConfig: widget.queryConfig,
      chartConfig: widget.chartConfig ?? null,
      timeOverride: widget.timeOverride ?? null,
      position: clampPosition(widget.position),
    }));
}

function serverIdsOf(widgets: readonly PlacedWidget[]): string[] {
  return widgets
    .map(widget => widget.serverId)
    .filter((serverId): serverId is string => !!serverId);
}

export interface WidgetSaveSnapshot {
  signatures: ReadonlyMap<string, string>;
  serverIds: Set<string>;
}

export function widgetSignature(widget: PlacedWidget): string {
  return JSON.stringify({
    title: widget.title,
    widgetType: widget.widgetType,
    queryConfig: widget.queryConfig,
    chartConfig: widget.chartConfig ?? null,
    timeOverride: widget.timeOverride ?? null,
    position: clampPosition(widget.position),
  });
}

@Service({ autoProvided: false })
export class DashboardEditSessionStore {
  private localIdCounter = 0;

  readonly boundId = signal<string | null>(null);
  /** True for a session opened on `/dashboards/new` and stays true after its first save creates the row. */
  readonly isNew = signal(false);
  readonly loadStatus = signal<DashboardEditLoadStatus>('idle');

  readonly placedWidgets = signal<PlacedWidget[]>([]);
  readonly widgetsDirty = signal(false);
  readonly originalWidgetIds = signal<string[]>([]);
  readonly loadedLayout = signal<unknown>(null);
  readonly formState = signal<DashboardFormState | null>(null);
  readonly metadataBaseline = signal<DashboardFormValue | null>(null);
  readonly createdBy = signal<string | null>(null);
  readonly updatedAt = signal<string | null>(null);

  readonly timeContext = signal<TimeOverrideConfig>(DEFAULT_TIME_CONTEXT);

  /** Round-tripped because the endpoint substitutes its own default when the field is absent from a save. */
  readonly loadedTimeContext = signal<TimeOverrideConfig>(DEFAULT_TIME_CONTEXT);

  readonly hasUnsavedChanges = computed(
    () => (this.formState()?.dirty ?? false) || this.widgetsDirty()
  );

  readonly metadata = computed(
    () => this.formState()?.value ?? this.metadataBaseline()
  );

  readonly metadataValid = computed(() => {
    const value = this.metadata();
    return value !== null && isMetadataValid(value);
  });

  readonly previewDashboardId = computed(
    () => this.boundId() ?? (this.isNew() ? NEW_DASHBOARD_PREVIEW_ID : null)
  );

  readonly editorRoute = computed<string[]>(() => {
    if (this.isNew()) return ['/dashboards', 'new'];
    const id = this.boundId();
    return id ? ['/dashboards', id, 'edit'] : ['/dashboards'];
  });

  markMetadataSaved(value: DashboardFormValue): void {
    this.metadataBaseline.set(value);
  }

  seed(
    dashboardId: string,
    dashboard: Dashboard,
    preferredTimeContext: TimeOverrideConfig | null = null
  ): void {
    const placed = toPlacedWidgets(dashboard.widgets);
    const stored = isValidTimeOverride(dashboard.defaultTimeContext)
      ? dashboard.defaultTimeContext
      : DEFAULT_TIME_CONTEXT;
    this.boundId.set(dashboardId);
    this.loadedLayout.set(dashboard.layout ?? null);
    this.placedWidgets.set(placed);
    this.originalWidgetIds.set(serverIdsOf(placed));
    this.widgetsDirty.set(false);
    this.metadataBaseline.set(toFormValue(dashboard));
    this.createdBy.set(dashboard.createdBy ?? null);
    this.updatedAt.set(dashboard.updatedAt ?? null);
    this.loadedTimeContext.set(stored);
    this.timeContext.set(preferredTimeContext ?? stored);
    this.loadStatus.set('loaded');
  }

  seedNew(createdBy: string | null): void {
    this.reset();
    this.isNew.set(true);
    this.createdBy.set(createdBy);
    this.metadataBaseline.set(newDashboardFormValue());
    this.loadedTimeContext.set(DEFAULT_TIME_CONTEXT);
    this.timeContext.set(DEFAULT_TIME_CONTEXT);
    this.loadStatus.set('loaded');
  }

  bindCreated(dashboardId: string): void {
    this.boundId.set(dashboardId);
  }

  editMetadata(patch: Partial<DashboardFormValue>): void {
    const baseline = this.metadataBaseline();
    const value = { ...(this.metadata() ?? newDashboardFormValue()), ...patch };
    this.setFormState({
      value,
      valid: isMetadataValid(value),
      dirty:
        !baseline ||
        JSON.stringify(normalizeMetadata(value)) !==
          JSON.stringify(normalizeMetadata(baseline)),
    });
  }

  commitMetadata(saved: DashboardFormValue): void {
    const live = this.metadata();
    this.metadataBaseline.set(normalizeMetadata(saved));
    if (!live || live === saved) {
      this.formState.set(null);
      return;
    }
    this.editMetadata({});
  }

  setTimeContext(config: TimeOverrideConfig): void {
    this.timeContext.set(config);
  }

  setLoadStatus(status: DashboardEditLoadStatus): void {
    this.loadStatus.set(status);
  }

  setFormState(state: DashboardFormState | null): void {
    this.formState.set(state);
  }

  addPending(definition: WidgetDefinition): void {
    const position = defaultPositionForNewTile(
      this.placedWidgets().map(widget => widget.position)
    );
    const placed: PlacedWidget = {
      localId: `pending-${++this.localIdCounter}`,
      serverId: null,
      title: definition.title,
      widgetType: definition.widget_type,
      queryConfig: definition.query_config,
      chartConfig: definition.chart_config ?? null,
      timeOverride: definition.time_override ?? null,
      position,
    };
    this.placedWidgets.update(widgets => [...widgets, placed]);
    this.widgetsDirty.set(true);
  }

  updatePositions(updates: PlacedWidgetPosition[]): void {
    const byLocalId = new Map(
      updates.map(update => [update.localId, update.position])
    );
    this.placedWidgets.update(widgets =>
      widgets.map(widget =>
        byLocalId.has(widget.localId)
          ? { ...widget, position: byLocalId.get(widget.localId)! }
          : widget
      )
    );
    this.widgetsDirty.set(true);
  }

  remove(localId: string): void {
    this.placedWidgets.update(widgets =>
      widgets.filter(widget => widget.localId !== localId)
    );
    this.widgetsDirty.set(true);
  }

  stampWidgetServerId(localId: string, serverId: string): void {
    this.placedWidgets.update(widgets =>
      widgets.map(widget =>
        widget.localId === localId ? { ...widget, serverId } : widget
      )
    );
  }

  updateWidget(localId: string, definition: WidgetDefinition): void {
    const exists = this.placedWidgets().some(
      widget => widget.localId === localId
    );
    if (!exists) return;
    this.placedWidgets.update(widgets =>
      widgets.map(widget =>
        widget.localId === localId
          ? {
              ...widget,
              title: definition.title,
              widgetType: definition.widget_type,
              queryConfig: {
                ...definition.query_config,
                sourceItemIdHistory: widget.queryConfig.sourceItemIdHistory,
                sourceCustomInputIdHistory:
                  widget.queryConfig.sourceCustomInputIdHistory,
                timeOverride:
                  definition.query_config.timeOverride ??
                  widget.queryConfig.timeOverride ??
                  null,
              },
              chartConfig: mergeChartConfig(
                widget.chartConfig,
                definition.chart_config ?? null
              ),
              timeOverride:
                definition.time_override ?? widget.timeOverride ?? null,
            }
          : widget
      )
    );
    this.widgetsDirty.set(true);
  }

  reconcileSaved(snapshot: WidgetSaveSnapshot): void {
    this.originalWidgetIds.set([...snapshot.serverIds]);
    const live = this.placedWidgets();
    const dirty =
      live.length !== snapshot.signatures.size ||
      live.some(
        widget =>
          snapshot.signatures.get(widget.localId) !== widgetSignature(widget)
      );
    this.widgetsDirty.set(dirty);
  }

  reset(): void {
    this.boundId.set(null);
    this.isNew.set(false);
    this.loadStatus.set('idle');
    this.placedWidgets.set([]);
    this.widgetsDirty.set(false);
    this.originalWidgetIds.set([]);
    this.loadedLayout.set(null);
    this.formState.set(null);
    this.metadataBaseline.set(null);
    this.createdBy.set(null);
    this.updatedAt.set(null);
  }

  serializeLayout(): unknown {
    const placed = this.placedWidgets();
    if (placed.length === 0) {
      const loadedLayout = this.loadedLayout();
      return Array.isArray(loadedLayout) ? loadedLayout : [];
    }
    return placed.map(widget => ({
      id: widget.serverId ?? widget.localId,
      ...clampPosition(widget.position),
    }));
  }

  buildUpdateRequest(layout: unknown): UpdateDashboardRequest {
    return {
      ...this.buildMetadataRequest(),
      defaultTimeContext: this.loadedTimeContext(),
      layout,
    };
  }

  buildCreateRequest(): CreateDashboardRequest {
    return {
      ...this.buildMetadataRequest(),
      defaultTimeContext: this.loadedTimeContext(),
    };
  }

  private buildMetadataRequest(): Omit<
    UpdateDashboardRequest,
    'defaultTimeContext' | 'layout'
  > {
    const value = normalizeMetadata(this.metadata() ?? newDashboardFormValue());
    return {
      title: value.title,
      description: value.description,
      visibility: value.visibility,
      departments: value.departments.length > 0 ? value.departments : null,
      assignedUsers:
        value.assignedUsers.length > 0 ? value.assignedUsers : null,
    };
  }
}
